import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Smartphone, Share, X } from "lucide-react";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}
function isMobileDevice() {
  return /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent);
}

export default function InstallPWAButton({ variant = "header" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !isMobileDevice()) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch {}
      setDeferred(null);
    } else {
      setHelp(true);
    }
  };

  const btnClass = variant === "header"
    ? "inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-semibold px-3 py-2 rounded-full hover:bg-slate-800 transition whitespace-nowrap"
    : "inline-flex items-center gap-2 bg-[#0F172A] text-white font-bold px-5 py-3 rounded-lg hover:bg-slate-800 transition w-full justify-center";

  return (
    <>
      <button onClick={click} className={btnClass} data-testid="install-app-btn" title="Installer l'application mobile">
        <Download className="w-4 h-4" />
        <span className={variant === "header" ? "hidden sm:inline" : ""}>Installer l'app</span>
      </button>

      {help && createPortal((
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="install-help">
          <div className="absolute inset-0 bg-black/50" onClick={() => setHelp(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 animate-fade-up max-h-[85vh] overflow-y-auto">
            <button onClick={() => setHelp(false)} className="absolute top-4 right-4 text-slate-400" data-testid="install-help-close"><X className="w-5 h-5" /></button>
            <div className="w-12 h-12 rounded-xl bg-[#FDF3EE] flex items-center justify-center mb-3"><Smartphone className="w-6 h-6 text-[#C85A32]" /></div>
            <h2 className="font-display text-2xl font-bold text-[#0F172A]">Installer TOGOBATI</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">Ajoutez l'application à votre écran d'accueil pour un accès rapide, même avec une connexion faible.</p>
            {isIOS() ? (
              <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
                <li>Appuyez sur le bouton <Share className="inline w-4 h-4 -mt-0.5" /> <strong>Partager</strong> dans Safari.</li>
                <li>Choisissez <strong>« Sur l'écran d'accueil »</strong>.</li>
                <li>Appuyez sur <strong>Ajouter</strong> — c'est prêt !</li>
              </ol>
            ) : (
              <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
                <li>Ouvrez le menu <strong>⋮</strong> de votre navigateur.</li>
                <li>Choisissez <strong>« Installer l'application »</strong> ou <strong>« Ajouter à l'écran d'accueil »</strong>.</li>
                <li>Confirmez l'installation.</li>
              </ol>
            )}
          </div>
        </div>
      ), document.body)}
    </>
  );
}
